/**
 * Channel-aware storage utilities + gate persistence helpers.
 *
 * In 'stable' channel, keys are unchanged.
 * In preview channels (e.g. 'canary'), keys are prefixed: `canary:app_theme`.
 * This prevents settings from bleeding across deploy channels.
 */

const GATE_SESSION_KEY = 'gateUnlocked';
const GATE_HASH_SESSION_KEY = 'gateHash';
const GATE_REMEMBER_KEY = 'gateRemember';
const REMEMBER_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/** Current deploy channel (injected at build time). */
export function getChannel(): string {
  try {
    return __CHANNEL__;
  } catch {
    return 'stable';
  }
}

/** True when running a non-stable channel (canary, beta, etc.). */
export function isPreviewChannel(): boolean {
  return getChannel() !== 'stable';
}

/**
 * Return a channel-namespaced storage key.
 * Stable channel returns the key as-is for backwards compatibility.
 */
export function storageKey(key: string): string {
  const ch = getChannel();
  return ch === 'stable' ? key : `${ch}:${key}`;
}

// ── Gate persistence ────────────────────────────────────────────────────

interface RememberPayload {
  hash: string;
  expiresAt: number;
}

/**
 * Check whether the gate is already unlocked (session or remembered).
 * Returns true if valid session OR non-expired localStorage remember-me matches.
 */
export function isGateUnlocked(expectedHash: string): boolean {
  if (!expectedHash) return false;

  // 1. Session check (fast path)
  if (
    sessionStorage.getItem(GATE_SESSION_KEY) === 'true' &&
    sessionStorage.getItem(GATE_HASH_SESSION_KEY) === expectedHash
  ) {
    return true;
  }

  // 2. Remember-me check
  try {
    const raw = localStorage.getItem(storageKey(GATE_REMEMBER_KEY));
    if (raw) {
      const payload: RememberPayload = JSON.parse(raw);
      if (payload.hash === expectedHash && payload.expiresAt > Date.now()) {
        // Re-hydrate session so subsequent checks are fast
        sessionStorage.setItem(GATE_SESSION_KEY, 'true');
        sessionStorage.setItem(GATE_HASH_SESSION_KEY, expectedHash);
        return true;
      }
      // Expired or wrong hash — clean up
      localStorage.removeItem(storageKey(GATE_REMEMBER_KEY));
    }
  } catch {
    // Malformed payload — ignore
  }

  return false;
}

/**
 * Mark the gate as unlocked.
 * Always writes to sessionStorage.
 * If `remember` is true, also writes to localStorage with a 7-day TTL.
 */
export function unlockGate(hash: string, remember: boolean): void {
  sessionStorage.setItem(GATE_SESSION_KEY, 'true');
  sessionStorage.setItem(GATE_HASH_SESSION_KEY, hash);

  if (remember) {
    const payload: RememberPayload = {
      hash,
      expiresAt: Date.now() + REMEMBER_TTL_MS,
    };
    localStorage.setItem(storageKey(GATE_REMEMBER_KEY), JSON.stringify(payload));
  }
}
